import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { scoreLabel, USE_CASES, PROPERTY_TYPES } from "@/lib/constants";
import { useSeo } from "@/hooks/useSeo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function colorForCompat(v) {
  if (v === "autorise") return "text-emerald-400";
  if (v === "peut-etre") return "text-amber-400";
  if (v === "non") return "text-zinc-600";
  return "text-zinc-400";
}

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", company: "", message: "", intended_use: "", visit_requested: false });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setListing(false));
  }, [id]);

  useSeo({
    title: listing && listing.title ? `${listing.title} — ${listing.city}` : "Annonce",
    description: listing && listing.title ? `${listing.title} : ${listing.surface_exploitable || listing.surface_total} m² à ${listing.city}, hauteur ${listing.ceiling_height_min}m. Score d'exploitabilité ${listing.score}/100 sur R-1.immo.` : undefined,
  });

  if (listing === null) return <div className="p-16 label-mono text-zinc-400">Chargement…</div>;
  if (listing === false) return <div className="p-16 label-mono text-zinc-400">Annonce introuvable</div>;

  const s = scoreLabel(listing.score || 0);
  const propType = PROPERTY_TYPES.find((p) => p.value === listing.property_type)?.label || listing.property_type;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...contactForm, listing_id: id });
      setSent(true);
      toast.success("Demande envoyée avec succès");
    } catch (err) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const toggleFavorite = async () => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris"); return; }
    try {
      await api.post("/favorites", { listing_id: id });
      toast.success("Ajouté aux favoris");
    } catch { toast.error("Erreur"); }
  };

  const displayAddress = () => {
    switch (listing.address_display) {
      case "exact": return listing.address_full;
      case "rue": return `${listing.address_full.split(",")[0] || ""}, ${listing.city}`;
      case "quartier": return `${listing.arrondissement || "Quartier"} — ${listing.city}`;
      default: return listing.city;
    }
  };

  return (
    <div>
      {/* Gallery */}
      <section className="border-b border-zinc-900">
        <div className="grid md:grid-cols-[1fr_320px] gap-px bg-zinc-800">
          <div className="aspect-[16/9] bg-zinc-950 relative">
            {listing.photos?.[activePhoto] ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${listing.photos[activePhoto]}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center label-mono text-zinc-700">[ AUCUNE PHOTO ]</div>
            )}
          </div>
          <div className="bg-zinc-950 p-6 flex flex-col justify-between">
            <div>
              <div className="label-mono mb-2 text-amber-500">{propType}</div>
              <h1 data-testid="listing-title" className="font-display text-3xl leading-tight mb-3">{listing.title}</h1>
              <div className="label-mono text-zinc-500 mb-6">{displayAddress()}</div>
              <div className={`font-mono-tech text-5xl ${s.color} mb-2`}>[ {listing.score} / 100 ]</div>
              <div className={`label-mono ${s.color}`} data-testid="exploitability-score">{s.label}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-zinc-800">
              <div>
                <div className="label-mono">Surface</div>
                <div className="font-display text-xl">{listing.surface_exploitable || listing.surface_total} m²</div>
              </div>
              <div>
                <div className="label-mono">Hauteur</div>
                <div className="font-display text-xl">{listing.ceiling_height_min} m</div>
              </div>
              <div>
                <div className="label-mono">Loyer</div>
                <div className="font-display text-xl">{listing.monthly_rent > 0 ? `${listing.monthly_rent}€` : "—"}</div>
              </div>
              <div>
                <div className="label-mono">Statut</div>
                <div className="font-display text-xl">{listing.is_erp ? "ERP" : "—"}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Thumbnails */}
        {listing.photos?.length > 1 && (
          <div className="flex gap-px bg-zinc-800 border-t border-zinc-800 overflow-x-auto">
            {listing.photos.map((p, i) => (
              <button key={p} onClick={() => setActivePhoto(i)} className={`shrink-0 w-24 aspect-[4/3] bg-zinc-950 ${i === activePhoto ? "ring-2 ring-amber-500" : "opacity-70 hover:opacity-100"}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="px-6 md:px-12 lg:px-16 py-16 grid lg:grid-cols-[1fr_360px] gap-12">
        <div className="space-y-12">
          {/* Description */}
          {listing.description && (
            <section>
              <div className="label-mono mb-3 text-amber-500">§ description</div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
            </section>
          )}

          {/* Tech data */}
          <section>
            <div className="label-mono mb-6 text-amber-500">§ données techniques</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              {[
                ["Type de bien", propType],
                ["Surface totale", `${listing.surface_total} m²`],
                ["Surface exploitable", `${listing.surface_exploitable} m²`],
                ["Niveaux sous-sol", listing.nb_niveaux],
                ["Hauteur min", `${listing.ceiling_height_min} m`],
                ["Hauteur moy.", `${listing.ceiling_height_avg} m`],
                ["Sol", listing.floor_type || "—"],
                ["Murs", listing.wall_type || "—"],
                ["État", listing.condition],
                ["Ventilation", listing.ventilation ? "Oui" : "Non"],
                ["Extraction", listing.extraction ? "Oui" : "Non"],
                ["Sanitaires", listing.toilets ? "Oui" : "Non"],
                ["Point d'eau", listing.kitchen ? "Oui" : "Non"],
                ["Fibre internet", listing.internet ? "Oui" : "Non"],
                ["Puissance élec.", `${listing.power_kva} kVA`],
                ["Triphasé", listing.triphase ? "Oui" : "Non"],
                ["Nombre sorties", listing.nb_exits],
                ["Humidité", listing.humidity ? "Oui" : "Non"],
              ].map(([k, v]) => (
                <div key={k} className="bg-zinc-950 p-3">
                  <div className="label-mono mb-1">{k}</div>
                  <div className="text-sm text-zinc-100">{v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Access */}
          <section>
            <div className="label-mono mb-6 text-amber-500">§ accès & logistique</div>
            <div className="flex flex-wrap gap-2">
              {[
                listing.access_independent && "Accès indépendant",
                listing.access_247 && "Accès 24/7",
                listing.delivery_possible && "Livraison",
                listing.vehicle_access && "Accès véhicule",
                listing.has_freight_elevator && "Monte-charge",
                listing.has_elevator && "Ascenseur",
                listing.pmr_access && "PMR",
              ].filter(Boolean).map((t) => (
                <span key={t} className="border border-amber-500/30 text-amber-500 label-mono px-3 py-1.5">{t}</span>
              ))}
            </div>
          </section>

          {/* Preferred uses */}
          {(listing.preferred_uses?.length > 0 || listing.preferred_uses_note) && (
            <section>
              <div className="label-mono mb-6 text-amber-500">§ usages préconisés par le propriétaire</div>
              {listing.preferred_uses?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {listing.preferred_uses.map((v) => {
                    const u = USE_CASES.find((x) => x.value === v);
                    return <span key={v} className="border border-amber-500 text-amber-500 label-mono px-3 py-1.5">✓ {u?.label || v}</span>;
                  })}
                </div>
              )}
              {listing.preferred_uses_note && (
                <p className="text-zinc-300 leading-relaxed italic">« {listing.preferred_uses_note} »</p>
              )}
            </section>
          )}

          {/* Compatible uses */}
          <section>
            <div className="label-mono mb-6 text-amber-500">§ usages compatibles</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              {USE_CASES.map((u) => {
                const v = listing.compatible_uses?.[u.value];
                if (!v) return null;
                const color = colorForCompat(v);
                return (
                  <div key={u.value} className="bg-zinc-950 p-3 flex items-center justify-between">
                    <span className="text-sm text-zinc-100">{u.label}</span>
                    <span className={`label-mono ${color}`}>{v}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Documents */}
          {listing.documents?.length > 0 && (
            <section>
              <div className="label-mono mb-6 text-amber-500">§ documents disponibles</div>
              <ul className="border border-zinc-800 divide-y divide-zinc-800">
                {listing.documents.map((d, i) => (
                  <li key={i} className="p-3 flex justify-between items-center">
                    <span className="text-sm text-zinc-100">{d.name || d.type}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.path}`} target="_blank" rel="noreferrer" className="text-amber-500 text-xs label-mono">TÉLÉCHARGER →</a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Sticky Contact */}
        <aside className="lg:sticky lg:top-24 h-fit border border-zinc-800 bg-zinc-950 p-6">
          <div className="label-mono mb-4 text-amber-500">§ contacter</div>
          {sent ? (
            <div className="text-emerald-400 label-mono py-8 text-center border border-emerald-500/30">
              DEMANDE ENVOYÉE — Nous revenons vers vous rapidement.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <input required placeholder="Nom" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" data-testid="contact-name" />
              <input required type="email" placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" data-testid="contact-email" />
              <input placeholder="Téléphone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" data-testid="contact-phone" />
              <input placeholder="Société" value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
              <select value={contactForm.intended_use} onChange={(e) => setContactForm({ ...contactForm, intended_use: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500">
                <option value="">Usage envisagé…</option>
                {USE_CASES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
              <textarea required rows="4" placeholder="Message" value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" data-testid="contact-message" />
              <label className="flex items-center gap-2 text-xs text-zinc-400">
                <input type="checkbox" checked={contactForm.visit_requested} onChange={(e) => setContactForm({ ...contactForm, visit_requested: e.target.checked })} />
                Je souhaite organiser une visite
              </label>
              <button type="submit" data-testid="submit-lead-btn" className="w-full bg-amber-500 text-black py-3 text-xs uppercase tracking-widest hover:bg-amber-400 transition-colors">
                Envoyer la demande
              </button>
              <button type="button" onClick={toggleFavorite} data-testid="favorite-btn" className="w-full border border-zinc-800 text-zinc-400 py-2 text-xs uppercase tracking-widest hover:text-white hover:border-zinc-600">
                ♡ Ajouter aux favoris
              </button>
            </form>
          )}
        </aside>
      </div>
    </div>
  );
}
